import React, { useLayoutEffect, useRef } from 'react';
import Modal from '../../Modal';
import { RSButton } from 'reactsymbols-kit';
import Styles from '../../../styles/colors';
import { stripIndent, source } from 'common-tags';
import { flatten } from 'lodash';
import { saveAs } from 'file-saver';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { SYMBOLS } from '../../../stores/model/metamodel/mpc_config';
import type { IMetaModel } from '../../../stores/model/metamodel';
import type { IObjectType } from '../../../stores/model/metamodel/object_type';
import type { IDependency } from '../../../stores/model/metamodel/dependency';
import type { IMultiplePropagationConstraint } from '../../../stores/model/metamodel/multiple_propagation_constraint';
import type { IAttribute } from '../../../stores/model/metamodel/attribute';
import type { IMethod } from '../../../stores/model/metamodel/method';
import { rsValue } from '../../Inspector/rsForm';

/** Props for the inner {@link LearningReport} content and its modal wrapper. */
interface LearningReportModalProps {
  /** Whether the modal is open. */
  isOpen: boolean;
  /** The metamodel to build the learning report from. */
  model: IMetaModel;
  /** The model name used for the saved file. */
  modelName: string;
  /** Close the modal. */
  onRequestClose: () => void;
}

/**
 * Show a learning report and allow to save as txt.
 *
 * Note: the class tracked a `saving` state that was read in render but never set
 * to true (save is synchronous), so the spinner branches were always inactive.
 * We reproduce that exactly with a constant `saving = false`.
 */
function LearningReport({ model, modelName, onRequestClose }: LearningReportModalProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const saving = false;

  // Generate the message for a dependency
  const dependencyMessage = (object: IObjectType, dep: IDependency, asMaster: boolean) => {
    if (asMaster) {
      switch (dep.type) {
        case 'OPTIONAL_N':
          return stripIndent`
          A ${object.name} object can have 0 or n dependent objects of type ${dep.dependent.name}.
          A ${object.name} object cannot be ended as long as it has dependent ${dep.dependent.name} objects.
        `;
        case 'OPTIONAL_1':
          return stripIndent`
            A ${object.name} object can have 0 or 1 dependent objects of type ${dep.dependent.name}.
            A ${object.name} object cannot be ended as long as it has a dependent ${dep.dependent.name} object.
          `;
        case 'MANDATORY_N':
          return stripIndent`
            A ${object.name} object must have 1 or n dependent objects of type ${dep.dependent.name}.
            A ${object.name} object cannot be ended as long as it has dependent ${dep.dependent.name} objects.
          `;
        case 'MANDATORY_1':
          return stripIndent`
            A ${object.name} object must have 1 dependent object of type ${dep.dependent.name}.
            A ${object.name} object cannot be ended as long as it has a dependent ${dep.dependent.name} object.
          `;
        default:
          // We shouldn't get here
          return 'Invalid dependency type';
      }
    } else {
      return stripIndent`
          A ${object.name} object can only exist in the context of exactly 1 object of type ${dep.master.name}: it refers to one and always the same ${dep.master.name}.
          A ${object.name} can only be created after the ${dep.master.name} object it refers to has been created.
        `;
    }
  };

  // Generate the message for the inheritances of an object type
  const inheritanceMessage = (object: IObjectType, asSupertype: boolean) => {
    if (asSupertype) {
      if (object.abstract) {
        return stripIndent`
        The class ${object.name} cannot have objects of its own.
        If a ${
          object.name
        } object is needed, an object belonging to one of the subclasses (${object.indirectSubtypes
          .map((o) => o.asString())
          .join(', ')}) will be used.
        `;
      } else {
        return stripIndent`
          The class ${object.name} can have objects of its own.
          Whenever a ${
            object.name
          } object is required, it can always be substituted by an object of one of the subclasses (${object.indirectSubtypes
            .map((o) => o.asString())
            .join(', ')}).
        `;
      }
    } else {
      // An object type can have at most 1 direct supertype because multiple
      // inheritance is not allowed
      const supertype = object.supertypes.length > 0 ? object.supertypes[0] : undefined;
      if (supertype) {
        return stripIndent`
          A ${object.name} object is a kind of ${supertype.name} object.
          A ${object.name} object is created in the class ${object.name} and will remain a ${supertype.name} object for its entire life.
          `;
      }
    }
  };

  const mpcMessage = (object: IObjectType, mpc: IMultiplePropagationConstraint) => {
    return `${object.name}.${mpc.primaryPath.information} ${SYMBOLS[mpc.type]} ${object.name}.${
      mpc.secondaryPath.information
    }`;
    // return mpc.displayString;
  };

  // Generate the message for an attribute. Mirrors what AttributePopover shows
  // in the Inspector: name + type, followed by description/validation
  // rule/implementation when present.
  const attributeMessage = (object: IObjectType, attribute: IAttribute) => {
    const lines = [`${object.name}.${attribute.name}: ${attribute.typeName}`];
    if (attribute.description) {
      lines.push(`  Description: ${attribute.description}`);
    }
    if (attribute.validation) {
      lines.push(`  Validation rule: ${attribute.validation}`);
    }
    if (attribute.implementation) {
      lines.push(`  Implementation: ${attribute.implementation}`);
    }
    return lines.join('\n');
  };

  // Generate the message for a method: which event it belongs to, its type
  // (create/modify/end) and its provenance (owned/acquired/inherited/specialised).
  const methodMessage = (object: IObjectType, method: IMethod) => {
    return `${object.name}.${method.name} (${method.ownerEventType.name}): ${method.type.toLowerCase()}, ${method.provenance.toLowerCase()}`;
  };

  // generate the text to save the inheritances for an object type
  const saveInheritance = (object: IObjectType) => {
    const messages: (string | undefined)[] = [];
    if (object.inheritancesAsSubtype.length > 0) {
      messages.push(inheritanceMessage(object, false));
    }
    if (object.inheritancesAsSupertype.length > 0) {
      messages.push(inheritanceMessage(object, true));
    }
    if (messages.length > 0) {
      return `${messages}\n`;
    } else {
      return '';
    }
  };

  // generate the text to save for an object type
  const saveObject = (object: IObjectType) => {
    return source`${object.name} exists as a master in the following relationships:
        ${flatten(
          object.dependenciesAsMaster
            .map((d) => dependencyMessage(object, d, true).split('\n'))
            .concat([''])
        )}
      ${object.name} exists as a dependent in the following relationships:
        ${flatten(
          object.dependenciesAsDependent
            .map((d) => dependencyMessage(object, d, false).split('\n'))
            .concat([''])
        )}
      ${object.name} has the following inheritance information:
        ${saveInheritance(object)}
      ${object.name} has the following multiple propagation constraints:
        ${object.multiplePropagationConstraints.map((mpc) => mpcMessage(object, mpc))}
      ${object.name} has the following attributes:
        ${flatten(
          object.attributes.map((a) => attributeMessage(object, a).split('\n')).concat([''])
        )}
      ${object.name} has the following methods:
        ${object.methods.map((m) => methodMessage(object, m))}
    `;
  };

  const save = () => {
    const report = source`
      MERODE learning report generated on ${new Date().toLocaleString()}

      ${model.objectTypes.map((o) => `${saveObject(o)}\n\n`)}
    `;
    return report;
  };

  // handler for the save button
  const onSave = () => {
    const report = save();
    const blob = new Blob([report.replace(/\n/g, '\r\n')], {
      type: 'text/plain',
    }); // replace: 'kladblok' on windows needs CRLF line endings
    saveAs(blob, `${modelName}_learning_report.txt`);
  };

  const renderDependency = (object: IObjectType, dep: IDependency, asMaster: boolean) => {
    return <li key={dep.id}>{dependencyMessage(object, dep, asMaster)}</li>;
  };

  const renderInheritance = (object: IObjectType, asSupertype: boolean) => {
    return (
      <li key={`${object.id}-${asSupertype}`}>{inheritanceMessage(object, asSupertype)}</li>
    );
  };

  const renderMPC = (object: IObjectType, mpc: IMultiplePropagationConstraint) => {
    return <li key={mpc.id}>{mpcMessage(object, mpc)}</li>;
  };

  const renderAttribute = (object: IObjectType, attribute: IAttribute) => {
    const lines = attributeMessage(object, attribute).split('\n');
    return (
      <li key={attribute.id}>
        {lines.map((line, index) => (
          <React.Fragment key={index}>
            {index > 0 && <br />}
            {line}
          </React.Fragment>
        ))}
      </li>
    );
  };

  const renderMethod = (object: IObjectType, method: IMethod) => {
    return <li key={method.id}>{methodMessage(object, method)}</li>;
  };

  const renderObject = (object: IObjectType) => {
    return (
      <div key={object.id} className="object-type">
        <div className="as-master">
          {object.name} exists as a master in the following relationships:
          {object.dependenciesAsMaster.length > 0 && (
            <ul className="dependencies">
              {object.dependenciesAsMaster.map((d) => renderDependency(object, d, true))}
            </ul>
          )}
        </div>
        <div className="as-dependent">
          {object.name} exists as a dependent in the following relationships:
          {object.dependenciesAsDependent.length > 0 && (
            <ul className="dependencies">
              {object.dependenciesAsDependent.map((d) => renderDependency(object, d, false))}
            </ul>
          )}
        </div>
        <div className="inheritance">
          {object.name} has the following inheritance information:
          {object.inheritances.length > 0 && (
            <ul className="inheritances">
              {object.inheritancesAsSubtype.length > 0 && renderInheritance(object, false)}
              {object.inheritancesAsSupertype.length > 0 && renderInheritance(object, true)}
            </ul>
          )}
        </div>
        <div className="mpcs">
          {object.name} has the following multiple propagation constraints:
          {object.multiplePropagationConstraints.length > 0 && (
            <ul className="mpc-list">
              {object.multiplePropagationConstraints.map((c) => renderMPC(object, c))}
            </ul>
          )}
        </div>
        <div className="attributes">
          {object.name} has the following attributes:
          {object.attributes.length > 0 && (
            <ul className="attributes-list">
              {object.attributes.map((a) => renderAttribute(object, a))}
            </ul>
          )}
        </div>
        <div className="methods">
          {object.name} has the following methods:
          {object.methods.length > 0 && (
            <ul className="methods-list">
              {object.methods.map((m) => renderMethod(object, m))}
            </ul>
          )}
        </div>
      </div>
    );
  };

  // Lock body scroll against the inner scroll container while open, unlock on
  // unmount. The class did this in cDM/cWU; useLayoutEffect runs before paint so
  // the lock applies without a scroll flash.
  useLayoutEffect(() => {
    const node = scrollContainerRef.current;
    if (node) {
      disableBodyScroll(node);
    }
    return () => {
      if (node) {
        enableBodyScroll(node);
      }
    };
  }, []);

  return (
    <div className="merlin-modal learning-report-modal">
      <div className="title">Learning report</div>
      <div className="modal-body">
        <div className="container">
          <div className="row">
            <div className="col">
              <div className="learning-report" ref={scrollContainerRef}>
                <div className="timestamp">generated on {new Date().toLocaleString()}</div>
                {model.objectTypes.map((o) => renderObject(o))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="button-wrapper">
        <RSButton
          value="Close"
          type="button"
          background={Styles.defaultGradient}
          color={Styles.darkTextColor}
          onClick={onRequestClose}
        />
        <RSButton
          background={Styles.blueGradient}
          type="submit"
          value={rsValue(saving ? null : 'Save')}
          onClick={onSave}
          iconName={rsValue(saving ? 'FaSpinner' : null)}
          iconSize={15}
          className={rsValue(saving ? 'spin' : null)}
        />
      </div>
    </div>
  );
}

// Wrap in a modal
const LearningReportModal = (props: LearningReportModalProps) => (
  <Modal isOpen={props.isOpen} disableBodyScroll={false}>
    <LearningReport {...props} />
  </Modal>
);

export default LearningReportModal;
